import { useIsInModal } from 'contexts/ModalProvider'
import { LabFeatureFlags } from 'constants/lab'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import { isLabCampaignTagPayload, isLabTakeoverModalPayload, isLabTakeoverUSPPayload } from 'lib/lab/dynamicPayload'

/**
 * Centralises campaign tag + USP visibility decisions so every component renders off the same rules.
 * Refer to `SuppressTagWhenUSPVisible` when onboarding new Site Takeover surfaces that need bespoke behaviour.
 */

interface UseSiteTakeoverCampaignUIStateOptions {
  campaignTagId?: string
  forceTagVisible?: boolean
  forceUSPVisible?: boolean
  suppressTagWhenUSPVisible?: boolean
}

interface SiteTakeoverCampaignUIState {
  targetedCampaignId: string | null
  uspPayload: App.LabTakeoverUSPPayload | null
  tagPayload: App.LabCampaignTagPayload | null
  modalPayload: App.LabTakeoverModalPayload | null
  shouldShowTag: boolean
  shouldShowUSP: boolean
  shouldShowModal: boolean
}

// Centralized hook for determining visibility and payloads of Site Takeover campaign tag and USP components.
// Override returned state using options as needed for custom visibility scenarios.
// Use this hook in all relevant components to avoid duplicating UI logic.
// Extend this hook with additional logic when introducing new supported campaign UI elements.
export const useSiteTakeoverCampaignUIState = (
  options: UseSiteTakeoverCampaignUIStateOptions = {},
): SiteTakeoverCampaignUIState => {
  const {
    campaignTagId,
    forceTagVisible = false,
    suppressTagWhenUSPVisible = false,
  } = options

  // fetch all UI related feature flags
  const campaignTagFeatureFlag = useLabFeatureFlag(LabFeatureFlags.enableOfferCampaignTag)
  const campaignUspFeatureFlag = useLabFeatureFlag(LabFeatureFlags.enableTakeoverUSP)
  const campaignModalFeatureFlag = useLabFeatureFlag(LabFeatureFlags.enableTakeoverModal)
  const isInModal = useIsInModal()

  const tagPayload = isLabCampaignTagPayload(campaignTagFeatureFlag?.value)
    ? campaignTagFeatureFlag.value
    : null

  const uspPayload = isLabTakeoverUSPPayload(campaignUspFeatureFlag?.value)
    ? campaignUspFeatureFlag.value
    : null

  const modalPayload = isLabTakeoverModalPayload(campaignModalFeatureFlag?.value)
    ? campaignModalFeatureFlag.value
    : null

  // what campaign id is the tag targeting
  const targetedCampaignId = tagPayload?.campaignId ?? null
  /**
     * Only show content when both the feature flag is enabled (or forced) and the campaign matches.
     * Forced props let legacy contexts or SSR “preview” flow bypass the rollout flags safely.
     */
  const hasCampaignMatch = campaignTagId
    ? tagPayload?.campaignId === campaignTagId
    : true

  const shouldShowUSP = !!uspPayload // if uspPayload is not null
    && hasCampaignMatch // if campaign matches the targeted campaign (or no specific campaign is targeted)
    && !!campaignUspFeatureFlag?.enabled // if uspFeatureFlag is enabled

  // Certain surfaces (e.g. Flash Hotel intro) hide the label when the USP is also visible.
  const shouldShowTag = !!tagPayload // if tagPayload is not null
    && tagPayload.campaignId === campaignTagId // if tagPayload.campaignId is the same as campaignTagId
    && (forceTagVisible || !!campaignTagFeatureFlag?.enabled) // if forceTagVisible is true or campaignTagFeatureFlag is enabled
    && !(shouldShowUSP && suppressTagWhenUSPVisible && !forceTagVisible) // hide tag when USP is visible and suppression is enabled (unless forced)

  const shouldShowModal = !!modalPayload // if modalPayload is not null
    && hasCampaignMatch // if campaign matches the targeted campaign (or no specific campaign is targeted)
    && !!campaignModalFeatureFlag?.enabled // if campaignModalFeatureFlag is enabled
    && !isInModal // if not in modal

  return {
    targetedCampaignId,
    tagPayload,
    uspPayload,
    shouldShowTag,
    modalPayload,
    shouldShowUSP,
    shouldShowModal,
  }
}
